import {
  DocumentTemplateType,
  MergeFieldDto,
  QueryFactory,
} from '@lmt/shared-types';
import { TemplateDto } from '@lmt/shared-types';
import { showFieldCodes } from '@lmt/unicorn-shared/app/components/documents/richedit/ShowCodesAndValuesSwitcher';

import { RichEditComponent } from '../../common/devexpress/RichEdit/RichEditComponent';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '../common/Loading';
import { mergeFieldCodeAsString } from './components/helpers';
import { MergeFieldList } from './components/MergeFieldList';
import { ImportExportTemplateButton } from './richedit/ImportExportTemplateButton';
import { OpenTemplatesRichEditMenuItem } from './richedit/OpenTemplatesRichEditMenuItem';
import { SaveTemplateRichEditButton } from './richedit/SaveTemplateRichEditButton';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';

import localStyles from './DocumentViewer.module.scss';
import { AddGDTHeaderRichEditButton } from './richedit/AddGDTHeaderRichEditButton';
import {
  DXR,
  Options,
  RichEdit,
} from '../../common/devexpress/RichEdit/dx-richedit';
import { DocumentViewerMode } from './types';
import { isDocumentNew } from './richedit/richedit-helper';
import { SaveTemplateAsRichEditButton } from './richedit/SaveTemplateAsRichEditButton';
import { DeleteTemplateRichEditButton } from './richedit/DeleteTemplateRichEditButton';
import { CreateNewDocument } from './richedit/CreateNewDocument';
import { disablePrintAndDownload } from './richedit/disablePrintAndDownload';

export const GDTOutgoingTemplateDesigner: React.FC = React.memo(
  function GDTOutgoingTemplateDesigner() {
    const templateId =
      pages.gdtOutgoingTemplatesWithId.useMatch()?.params?.templateId;
    const richEdit = useRef<RichEdit>(null!);
    const i18n = useTranslation();
    const tabs = useTabs();
    const [isRichEditLoaded, setIsRichEditLoaded] = useState(false);
    const isLoading = useRef(true);

    const onLoaded = useCallback(() => {
      setIsRichEditLoaded(true);
    }, []);
    const onInit = useCallback((options: Options) => {
      options.view!.viewType = DXR.ViewType.Simple;
      //options.view!.simpleViewSettings!.fixedWidth = 2000;

      options.ribbon.removeTab(DXR.RibbonTabType.View);
      options.ribbon.removeTab(DXR.RibbonTabType.MailMerge);
      options.ribbon.removeTab(DXR.RibbonTabType.PageLayout);
      options.ribbon.removeTab(DXR.RibbonTabType.References);
      options.ribbon.removeTab(DXR.RibbonTabType.Insert);
      options.ribbon.removeTab(DXR.RibbonTabType.Home);
      const filePanel = options.ribbon.getTab(DXR.RibbonTabType.File)!;
      filePanel.removeItem(DXR.FileTabItemId.OpenDocument);
      disablePrintAndDownload(options);
      options.events.documentLoaded = (richEdit, e) => {
        if (!isLoading.current && isDocumentNew(richEdit)) {
          tabs.changeTabPath(pages.gdtOutgoingTemplates.link(), false);
          setCurrentTemplate(null);
        }
        isLoading.current = false;
      };
    }, []);

    const { data: mergeFields, isFetching: isMergeFieldsLoading } =
      QueryFactory.MergeFieldQuery.useGetQuery();

    const [currentTemplate, setCurrentTemplate] = useState<TemplateDto | null>(
      null,
    );

    useEffect(
      function UpdateMergeFieldValuesInDocumentOnLoad() {
        if (!isRichEditLoaded) return;
        const handler = () => {
          if (!mergeFields) return;

          showFieldCodes(richEdit.current, mergeFields);
        };
        richEdit.current.events.documentLoaded.addHandler(handler);
        return () => {
          richEdit.current.events.documentLoaded.removeHandler(handler);
        };
      },
      [mergeFields, isRichEditLoaded],
    );

    const onInsertMergeField = useCallback((mergeField: MergeFieldDto) => {
      const selection = richEdit.current.selection;
      const subDocument = selection.activeSubDocument;
      let position = selection.active;

      // if `position` is inside an existing DOCVARIABLE, insert new field AFTER existing
      const fieldsAtPosition = subDocument.fields.find(position);
      if (fieldsAtPosition.length > 0) {
        position = fieldsAtPosition[0].resultInterval.end + 1;
      }
      const mergeFieldCode = mergeFieldCodeAsString(mergeField);
      const interval = subDocument.fields.create(
        position,
        'DOCVARIABLE ' + mergeFieldCode,
      ).interval;
      selection.setSelection(interval.end);
      richEdit.current.focus();
    }, []);

    const onTemplateOpened = useCallback((template: TemplateDto | null) => {
      if (!template) {
        return;
      }
      setCurrentTemplate(template);
      tabs.changeTabPath(
        pages.gdtOutgoingTemplatesWithId.link({
          templateId: template.id,
        }),
        false,
      );
    }, []);

    return (
      <div
        className={localStyles.gridWrapperDesigner}
        data-test-id={'gdt-designer'}
      >
        <div className={localStyles.richeditWrapper}>
          <RichEditComponent
            initialize={onInit}
            onLoaded={onLoaded}
            richEditRef={richEdit}
          />
          {isRichEditLoaded && (
            <>
              <CreateNewDocument richEdit={richEdit} />
              <SaveTemplateRichEditButton
                richEdit={richEdit}
                templateType={DocumentTemplateType.GDTOutgoingMessage}
                currentTemplate={currentTemplate}
                setCurrentTemplate={onTemplateOpened}
              />
              <SaveTemplateAsRichEditButton richEdit={richEdit} />
              <OpenTemplatesRichEditMenuItem
                richEdit={richEdit}
                onTemplateOpened={onTemplateOpened}
                templateType={DocumentTemplateType.GDTOutgoingMessage}
                templateId={templateId}
                mode={DocumentViewerMode.Designer}
              />
              <ImportExportTemplateButton
                richEdit={richEdit}
                currentTemplate={currentTemplate}
                templateType={DocumentTemplateType.GDTOutgoingMessage}
                tab={DXR.RibbonTabType.File}
              />

              <AddGDTHeaderRichEditButton
                templateId={currentTemplate?.id}
                richEdit={richEdit}
              />
              {currentTemplate && (
                <DeleteTemplateRichEditButton
                  richEdit={richEdit}
                  currentTemplate={currentTemplate}
                  templateType={DocumentTemplateType.GDTOutgoingMessage}
                />
              )}
            </>
          )}
        </div>
        <div className={localStyles.mergeFieldsWrapper}>
          <Loading
            loading={isMergeFieldsLoading}
            title={i18n.t('load.loading')}
          >
            <MergeFieldList onInsertMergeField={onInsertMergeField} />
          </Loading>
        </div>
      </div>
    );
  },
);
